import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environment/environment';
import { Contract } from './model/contract';
import { PageResult } from '../../common/model/PageResult';
import { ContractStatus } from './enum/contract-status.enum';
import { ContractQuery } from './model/contract-query';
import { DocumentService } from '../../common/service/document.service';
import { NotificationService } from '../../common/service/notification.service';
import { FileAttachment } from '../file-list/model/file-attachment';
import { withCache } from '../../core/cache/http-cache.interceptor';
import { ExternalContract } from '../external-contract/model/external-contract';

@Injectable({
  providedIn: 'root',
})
export class ContractService {
  private httpContext = inject(HttpClient);
  private documentService = inject(DocumentService);
  private notificationService = inject(NotificationService);
  private contractServiceEndpoint = environment.APIBase + 'Contract'; // Base URL for the contract service

  /**
   * Retrieves a paginated list of contracts based on the provided query parameters.
   * @param contractQuery - An ContractQuery object containing the query parameters for filtering and pagination.
   * @returns A promise that resolves with a PageResult<Contract> containing the paginated contracts and pagination details.
   */
  public getContracts(contractQuery: ContractQuery): Promise<PageResult<Contract>> {
    return new Promise<PageResult<Contract>>((resolve, reject) => {
      let headers = new HttpHeaders().set('timeout', '600000'); // Set query parameters
      this.httpContext
        .post(this.contractServiceEndpoint + '/query', contractQuery,
          {
            headers,
            ...withCache({
              cachePost: true,  // Enable caching for this POST request
              ttl: 300000       // Cache for 5 minutes (300000ms)
            })
          }
        )
        .pipe(map((res) => res as PageResult<Contract>)) // Map response to PageResult<Contract>
        .subscribe(
          (res: PageResult<Contract>) => {
            resolve(res); // Resolve with the paginated contracts
          },
          (error) => {
            console.error('Error in getContracts', error); // Log error
            reject(error); // Reject promise with error
          },
        );
    });
  }

  /**
   * Fetches the details of an contract based on the provided document number.
   * @param contractNo - The unique identifier (document number) used to fetch the contract details.
   * @returns A promise that resolves with an Contract object containing the details of the contract.
   */
  public getContractDetailByDocNo(contractNo: string): Promise<Contract> {
    const params = new HttpParams().set('contractNo', contractNo); // Set query parameters

    return new Promise<Contract>((resolve, reject) => {
      this.httpContext
        .get<Contract>(this.contractServiceEndpoint, { params })
        .pipe(map((res) => res as Contract)) // Map response to Contract
        .subscribe(
          (res: Contract) => {
            resolve(res); // Resolve with the fetched contract details
          },
          (error) => {
            console.error('Error in fetching contract details by document number', error); // Log error
            const backendError = error?.error; // this is the big stack trace string
            if (
              typeof backendError === 'string' &&
              backendError.includes("don't have any permission to access the document")
            ) {
              reject('NoPermission');
            } else {
              console.error('Error in getSearchDocument', error);
              reject(error);
            }
          },
        );
    });
  }


  /**
   * Downloads a contract file from the server and triggers a local file download.
   * Handles empty responses, errors, and notification messages.
   *
   * @param file ContractFile object containing id, filename, mimetype, etc.
   * @returns Promise<boolean> - true if file downloaded, false otherwise
   */

  public downloadContractFile(file: FileAttachment): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders().set('Accept', 'application/octet-stream');

      this.httpContext
        .get(this.contractServiceEndpoint + '/file/download?fileId=' + file.id, {
          headers,
          responseType: 'blob',
        })
        .subscribe(
          (blob) => {
            if (!blob || blob.size === 0) {
              this.notificationService.showNotification(
                'warn',
                'notification.file.fileDownloaded.emptyData.title',
                'notification.file.fileDownloaded.emptyData.message'
              );
              resolve(false);
              return;
            }

            // ✔ Blob has data — download file
            const fileName = file.filename;
            const mimeType = file.mimetype;

            this.documentService
              .downloadFile(fileName, blob, mimeType)
              .then((res) => resolve(true))
              .catch((err) => reject(err));
          },
          (error) => {
            if (error.status === 404) {
              this.notificationService.showNotification(
                'warn',
                'notification.file.fileDownloaded.emptyData.title',
                'notification.file.fileDownloaded.emptyData.message'
              );
            } else {
              this.notificationService.showNotification(
                'error',
                'notification.file.fileDownloaded.error.title',
                'notification.file.fileDownloaded.error.message'
              );
            }
            reject(error);
          }
        );
    });
  }

  /**
 * Updates customer visibility for a contract file.
 * @param entityNumber File entity number
 * @param isVisible Visibility flag
 * @returns Promise<void>
 */
  public updateFileVisibility(
    entityNumber: number,
    isVisible: boolean
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.httpContext.put(
        `${this.contractServiceEndpoint}/file/visibility`,
        null,
        {
          params: {
            entityNumber: entityNumber,
            isVisible: isVisible
          }
        }
      ).subscribe({
        next: () => {
          resolve();
        },
        error: (err) => {
          console.error('Error in updateFileVisibility', err);
          reject(err);
        }
      });
    });
  }

  /**
   * Fetches the external contracts.
   * @param customerNo - The customer number to filter external contracts
   * @returns A promise that resolves with an array of ExternalContract.
   */
  public getExternalContracts(customerNo: string): Promise<ExternalContract[]> {
    const params = new HttpParams().set('customerNo', customerNo);
    return new Promise<ExternalContract[]>((resolve, reject) => {
      this.httpContext
        .get<ExternalContract[]>(this.contractServiceEndpoint + '/externals', { params })
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => {
            console.error('Error in getExternalContracts', err);
            reject(err);
          }
        });
    });
  }
}


